import { useCallback, useEffect, useMemo, useRef } from 'react';
import { isNullish } from 'remeda';
import { useBrands } from 'src/graphql';
import { useConfirmationModal } from 'src/utils/hooks';

const UNSAVED_CHANGES_MESSAGE = 'You have unsaved changes. Continue without saving?';

interface UseClientSegmentsParams {
  partnerId: number | null;
  selectedSegmentId: string | null;
  onSegmentChange: (segmentId: string | null) => void | Promise<void>;
  hasUnsavedChanges: boolean;
}

export const useClientSegments = ({
  partnerId,
  selectedSegmentId,
  onSegmentChange,
  hasUnsavedChanges,
}: UseClientSegmentsParams) => {
  const { confirm } = useConfirmationModal();
  const { brands: brandsFromQuery, loading: segmentsLoading } = useBrands(partnerId);
  const segments = useMemo(
    () =>
      [...(brandsFromQuery ?? [])].sort((a, b) => (b.name ?? '').localeCompare(a.name ?? '')),
    [brandsFromQuery],
  );
  const hasPartner = !isNullish(partnerId);
  const hasSegments = segments.length > 0;
  const initialSegmentSelectedForPartnerRef = useRef<number | null>(null);

  const handleSegmentChange = useCallback(
    async (segmentId: string | null) => {
      const normalizedSegmentId = segmentId ? segmentId : null;

      if (normalizedSegmentId === selectedSegmentId) {
        return true;
      }

      if (hasUnsavedChanges) {
        const confirmed = await confirm({
          body: UNSAVED_CHANGES_MESSAGE,
        });

        if (!confirmed) {
          return false;
        }
      }

      await onSegmentChange(normalizedSegmentId);
      return true;
    },
    [confirm, hasUnsavedChanges, onSegmentChange, selectedSegmentId],
  );

  useEffect(() => {
    initialSegmentSelectedForPartnerRef.current = null;
  }, [partnerId]);

  useEffect(() => {
    if (!hasPartner || segmentsLoading || !hasSegments || selectedSegmentId) {
      return;
    }

    if (initialSegmentSelectedForPartnerRef.current === partnerId) {
      return;
    }

    initialSegmentSelectedForPartnerRef.current = partnerId;
    const defaultSegmentId = String(segments[0].brandId);
    void handleSegmentChange(defaultSegmentId);
  }, [
    handleSegmentChange,
    hasSegments,
    hasPartner,
    partnerId,
    segments,
    segmentsLoading,
    selectedSegmentId,
  ]);

  // The auto-selection above lands a render later; keep the skeleton until it does.
  const isInitialSegmentSelectionPending =
    hasPartner &&
    !segmentsLoading &&
    hasSegments &&
    !selectedSegmentId &&
    initialSegmentSelectedForPartnerRef.current !== partnerId;

  return {
    segments,
    segmentsLoading,
    hasPartner,
    hasSegments,
    handleSegmentChange,
    isInitialSegmentSelectionPending,
  };
};
